if (!customElements.get('bundle-set')) {
  customElements.define(
    'bundle-set',
    class BundleSet extends HTMLElement {
      connectedCallback() {
        // product-form.js re-enables the hidden variant-id input on load; this
        // component runs after it (both deferred, product-form.js is included
        // earlier in the section) and re-applies the pending state in update().
        this.form = document.getElementById(this.dataset.formId);
        this.variantInput = this.form ? this.form.querySelector('.product-variant-id') : null;
        this.submitButton = this.form ? this.form.querySelector('button[type="submit"]') : null;
        try {
          this.bundles = JSON.parse(this.querySelector('[data-bundle-set-data]').textContent).bundles;
        } catch (error) {
          console.error('bundle-set: invalid data JSON', error);
          this.bundles = [];
        }
        this.rows = Array.from(this.querySelectorAll('[data-row]')).filter((el) => el.matches('.bundle-set__row'));
        this.state = { top: null, bottom: null };
        const info = this.closest('product-info') || document;
        this.priceEl = info.querySelector('.price__regular .price-item--regular');
        this.originalPrice = this.priceEl ? this.priceEl.textContent : null;
        const saleEl = info.querySelector('.price__sale .price-item--sale');
        const compareEl = info.querySelector('.price__sale s.price-item--regular');
        this.originalSale = saleEl ? saleEl.textContent : null;
        this.originalCompare = compareEl ? compareEl.textContent : null;

        this.rows.forEach((row) => {
          row.querySelectorAll('[data-card]').forEach((card) => {
            card.addEventListener('click', () => this.selectCard(row, card));
          });
          row.querySelector('[data-row-reset]').addEventListener('click', () => this.resetRow(row));
        });
        this.addEventListener('change', (event) => {
          if (event.target.matches('input[type="radio"]')) this.update();
        });

        this.update();
      }

      rowName(row) {
        return row.dataset.row;
      }

      selectCard(row, card) {
        const name = this.rowName(row);
        const handle = card.dataset.handle;
        if (this.state[name] === handle) return;
        this.state[name] = handle;
        this.clearRowInputs(row);
        this.update();
        this.autoSelectDefaults(row);
      }

      autoSelectDefaults(row) {
        row.querySelectorAll('fieldset[data-option-name]').forEach((group) => {
          if (group.hidden || group.querySelector('input:checked')) return;
          const first = Array.from(group.querySelectorAll('input[type="radio"]')).find((input) => !input.disabled);
          if (first) {
            first.checked = true;
            this.update();
          }
        });
      }

      resetRow(row) {
        this.state[this.rowName(row)] = null;
        this.clearRowInputs(row);
        this.update();
      }

      clearRowInputs(row) {
        row.querySelectorAll('input[type="radio"]').forEach((input) => (input.checked = false));
      }

      candidateBundles(partial) {
        return this.bundles.filter(
          (bundle) =>
            (partial.top === null || bundle.top === partial.top) &&
            (partial.bottom === null || bundle.bottom === partial.bottom)
        );
      }

      activeBundle() {
        if (!this.state.top || !this.state.bottom) return null;
        return this.candidateBundles(this.state)[0] || null;
      }

      collectChecked() {
        const checked = {};
        this.querySelectorAll('fieldset[data-option-name]').forEach((group) => {
          const input = group.querySelector('input:checked');
          if (input) checked[group.dataset.optionName] = input.value;
        });
        return checked;
      }

      variantMatches(bundle, variant, overrides, checked) {
        return bundle.optionNames.every((optionName, index) => {
          const wanted = Object.prototype.hasOwnProperty.call(overrides, optionName)
            ? overrides[optionName]
            : Object.prototype.hasOwnProperty.call(checked, optionName)
            ? checked[optionName]
            : null;
          return wanted === null || variant.options[index] === wanted;
        });
      }

      bundleHasAvailable(bundle, overrides, checked) {
        return bundle.variants.some(
          (variant) => variant.available && this.variantMatches(bundle, variant, overrides, checked)
        );
      }

      update() {
        const checked = this.collectChecked();
        this.rows.forEach((row) => {
          const name = this.rowName(row);
          const selectedHandle = this.state[name];
          const otherName = name === 'top' ? 'bottom' : 'top';
          row.classList.toggle('has-selection', selectedHandle !== null);
          row.querySelector('[data-row-reset]').hidden = selectedHandle === null;

          row.querySelectorAll('[data-card]').forEach((card) => {
            const handle = card.dataset.handle;
            card.classList.toggle('is-selected', handle === selectedHandle);
            card.setAttribute('aria-pressed', handle === selectedHandle ? 'true' : 'false');
            const partial = { top: null, bottom: null };
            partial[name] = handle;
            partial[otherName] = this.state[otherName];
            const candidates = this.candidateBundles(partial);
            const unavailable =
              candidates.length === 0 || !candidates.some((bundle) => this.bundleHasAvailable(bundle, {}, checked));
            card.classList.toggle('is-unavailable', unavailable);
          });

          row.querySelectorAll('[data-component]').forEach((el) => {
            el.hidden = selectedHandle === null || el.dataset.component !== selectedHandle;
          });

          row.querySelectorAll('fieldset[data-option-name]').forEach((group) => {
            if (group.hidden) return;
            const optionName = group.dataset.optionName;
            const partial = { top: null, bottom: null };
            partial[name] = selectedHandle;
            partial[otherName] = this.state[otherName];
            const candidates = this.candidateBundles(partial).filter((bundle) =>
              bundle.optionNames.includes(optionName)
            );
            group.querySelectorAll('input[type="radio"]').forEach((input) => {
              const overrides = {};
              overrides[optionName] = input.value;
              const ok = candidates.some((bundle) =>
                bundle.variants.some(
                  (variant) => variant.available && this.variantMatches(bundle, variant, overrides, checked)
                )
              );
              input.disabled = !ok;
            });
          });
        });

        const bundle = this.activeBundle();
        let variant = null;
        if (bundle) {
          const complete = bundle.optionNames.every((optionName) => checked[optionName] !== undefined);
          if (complete) {
            variant = bundle.variants.find((v) => this.variantMatches(bundle, v, {}, checked)) || null;
          }
        }

        if (variant && variant.available) {
          if (this.variantInput) {
            this.variantInput.value = variant.id;
            this.variantInput.disabled = false;
          }
          if (this.submitButton) {
            this.submitButton.disabled = false;
            this.submitButton.classList.remove('button--bundle-pending');
          }
          this.updatePrice(variant);
        } else {
          if (this.variantInput) {
            this.variantInput.value = '';
            this.variantInput.disabled = true;
          }
          if (this.submitButton) {
            this.submitButton.disabled = true;
            this.submitButton.classList.add('button--bundle-pending');
          }
          this.updatePrice(null);
        }
      }

      updatePrice(variant) {
        // Пока комбинация не собрана целиком, цены на странице нет вовсе —
        // CSS прячет блок цены, пока на конструкторе нет этого класса.
        this.classList.toggle('bundle-set--priced', Boolean(variant));

        const info = this.closest('product-info') || document;
        const saleEl = info.querySelector('.price__sale .price-item--sale');
        const compareEl = info.querySelector('.price__sale s.price-item--regular');
        if (variant) {
          if (this.priceEl) this.priceEl.textContent = variant.priceFormatted;
          if (saleEl) saleEl.textContent = variant.priceFormatted;
          if (compareEl && variant.compareFormatted) compareEl.textContent = variant.compareFormatted;
        } else {
          if (this.priceEl && this.originalPrice !== null) this.priceEl.textContent = this.originalPrice;
          if (saleEl && this.originalSale !== undefined && this.originalSale !== null) saleEl.textContent = this.originalSale;
          if (compareEl && this.originalCompare !== undefined && this.originalCompare !== null) compareEl.textContent = this.originalCompare;
        }
      }
      // Ограничение (осознанное): если у витринного товара нет sale-разметки цены
      // (.price__sale скрыт темой), зачёркнутый compare-at не появится — показываем
      // только цену набора. Проще всего дать витринному товару свои
      // price/compare-at, совпадающие с бандлами, — тогда тема сама рендерит
      // sale-layout.
    }
  );
}
